import { useAuth } from 'context/AuthContext';
import useChats from 'hooks/useChats';
import useGetAllChats from 'hooks/useGetAllChats';
import React, {
  type ReactNode,
  createContext,
  useEffect,
  useCallback,
  useRef,
  useState,
  useContext,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import { isDev, type OnSocketNewMessage, SocketResponse } from '@/types';

const clearedHost = window.location.host.replace(/:\d+/, '');

export const SOCKET_URL = isDev
  ? `${process.env.REACT_APP_BACKEND_HOST}:${process.env.REACT_APP_BACKEND_PORT}`
  : `https://${clearedHost}`;

export const SocketContext = createContext<{
  socket: Socket | null;
  emitWithToken: <T, E = string>(event: string, data: unknown) => Promise<T>;
  requestNotificationPermission: () => void;
} | null>(null);

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [chatId, setChatId] = useState<number | null>(null);
  const { isAuthenticated, accessToken } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const { chatRefetch } = useChats(chatId);
  const { refetch: refetchAllChats } = useGetAllChats();
  const { profile } = useAuth();

  // Функція для запиту дозволу на повідомлення
  const requestNotificationPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        // console.log('Notification permission:', permission);
      });
    }
  }, []);

  // Функція для показу браузерного повідомлення
  const showNotification = useCallback((sender: any, data?: any) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      // Перевіряємо чи сторінка активна (користувач дивиться на неї)
      if (!document.hidden) {
        return; // Не показуємо повідомлення якщо користувач на сторінці
      }

      const senderName =
        sender.firstname && sender.lastname
          ? `${sender.firstname} ${sender.lastname}`
          : sender.email || 'Невідомий користувач';

      // Пробуємо отримати текст повідомлення з різних можливих полів
      const messageText =
        data?.message?.content ||
        data?.message?.text ||
        data?.content ||
        data?.text ||
        'Отримано нове повідомлення';

      const notification = new Notification(
        `Нове повідомлення від ${senderName}`,
        {
          body: messageText,
          icon: sender.avatar || '/favicon.ico',
          badge: '/favicon.ico',
          tag: `chat-${sender.id}`, // Щоб не дублювати повідомлення від того самого користувача
          requireInteraction: false, // Автоматично зникне
        },
      );

      // Клік на повідомлення - фокус на вкладку
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Автоматично закрити через 5 секунд
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }, []);

  useEffect(() => {
    // Запитуємо дозвіл при ініціалізації
    requestNotificationPermission();
  }, [requestNotificationPermission]);

  useEffect(() => {
    // временно для отладки
    // console.log('=== Socket URL Debug ===');
    // console.log('isDev:', isDev);
    // console.log('REACT_APP_BACKEND_HOST:', process.env.REACT_APP_BACKEND_HOST);
    // console.log('REACT_APP_BACKEND_PORT:', process.env.REACT_APP_BACKEND_PORT);
    // console.log('Final SOCKET_URL:', SOCKET_URL);
    // console.log('accessToken:', accessToken);
    // console.log('========================');

    if (!accessToken) {
      // console.log('No token');
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const token = `Bearer ${accessToken}`;

    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      extraHeaders: { Authorization: token },
      auth: { token },
      reconnectionAttempts: 3,
    });

    socketRef.current = socket;

    const handleNewMessage = (data: OnSocketNewMessage) => {
      // console.log('New message received:', data);
      const { chatId, sender } = data;

      if (sender && profile && sender.id !== profile.id) {
        setChatId(sender.id);
        chatRefetch();
        refetchAllChats();

        // Показуємо браузерне повідомлення (передаємо весь data об'єкт)
        showNotification(sender, data);
      }
    };

    const handleConnect = () => {
      // console.log('Connected to Socket.IO');
    };

    const handleDisconnect = () => {
      // console.log('Disconnected from Socket.IO');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.disconnect();
    };
  }, [
    accessToken,
    isAuthenticated,
    chatRefetch,
    refetchAllChats,
    profile,
    showNotification,
  ]);

  const emitWithToken = useCallback(
    <T, E = string>(event: string, data: unknown): Promise<T> => {
      return new Promise((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket) return reject('Socket not connected');

        const token = `Bearer ${localStorage.getItem('accessToken')}`;
        socket.auth = { token, ...socket.auth };

        socket.emit(event, data, (response: SocketResponse<T, E>) => {
          // console.debug('emitWithToken response', response);
          if (response.error) {
            reject(response.error);
          } else {
            resolve(response.data);
          }
        });
      });
    },
    [],
  );

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        emitWithToken,
        requestNotificationPermission,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
